import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router";
import { ArrowRight, CheckCircle2, Layers } from "lucide-react";

const BULLETS = [
  "Understand your situation",
  "Learn your options",
  "Get expert help when you're ready",
];

const IMG_PRIMARY =
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=85";
const IMG_SECONDARY =
  "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=85";

/**
 * “What is InfoBoxx?” — copy block + diagonal overlapping image orbs (venn-style).
 */
const WhatIsInfoBoxx = () => {
  return (
    <section className="bg-[#f7f4ec] px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
        {/* ── Venn-style diagonal image pair ───────────────── */}
        <motion.div
          className="relative mx-auto w-full max-w-lg lg:mx-0"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative aspect-square max-h-[min(100vw,28rem)] sm:max-h-[32rem]">
            {/* Back orb — top-left, tilted */}
            <motion.div
              className="absolute left-0 top-[2%] h-[62%] w-[62%] sm:h-[58%] sm:w-[58%]"
              initial={{ scale: 0.92, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative h-full w-full -rotate-[8deg] rounded-full border-4 border-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.45)] ring-1 ring-black/10">
                <img
                  src={IMG_PRIMARY}
                  alt="Planning and research at a desk"
                  className="h-full w-full rounded-full object-cover"
                />
                <div className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-tr from-black/25 via-transparent to-transparent" />
              </div>
            </motion.div>

            {/* Front orb — bottom-right, overlaps (venn intersection zone) */}
            <motion.div
              className="absolute bottom-[2%] right-0 h-[62%] w-[62%] z-10 sm:h-[58%] sm:w-[58%]"
              initial={{ scale: 0.92, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.18, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative h-full w-full rotate-[10deg] rounded-full border-4 border-[#FFD221]/70 shadow-[0_24px_70px_-22px_rgba(201,160,0,0.55)] ring-2 ring-black/5">
                <img
                  src={IMG_SECONDARY}
                  alt="Team collaborating on a project"
                  className="h-full w-full rounded-full object-cover"
                />
                <div className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-bl from-transparent via-transparent to-black/20" />
              </div>
            </motion.div>

            {/* Intersection accent — suggests shared “middle” of the journey */}
            <div
              className="pointer-events-none absolute left-[28%] top-[36%] z-[5] h-[38%] w-[38%] rounded-full bg-[#FFD221]/25 blur-2xl"
              aria-hidden
            />

            <motion.div
              className="absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full border border-black/10 bg-white/95 px-4 py-2.5 shadow-[0_12px_40px_-16px_rgba(0,0,0,0.35)] backdrop-blur-md"
              initial={{ scale: 0.85, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, type: "spring", stiffness: 260, damping: 22 }}
            >
              <Layers size={16} className="text-[#c9a000]" strokeWidth={2.5} aria-hidden />
              <span className="text-[11px] font-black uppercase tracking-wide text-gray-900">
                Question → clarity → action
              </span>
            </motion.div>
          </div>
        </motion.div>

        {/* ── Copy ─────────────────────────────────────────── */}
        <div>
          <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-[#c9a000]">
            <span className="inline-flex h-2 w-2 rounded-full bg-[#FFD221] shadow-sm ring-2 ring-[#FFD221]/40" aria-hidden />
            About the platform
          </p>
          <h2 className="mt-3 text-4xl font-black leading-tight text-black sm:text-5xl">
            What is InfoBoxx?
          </h2>
          <p className="mt-5 text-base leading-relaxed text-gray-700">
            InfoBoxx is a smart information platform that sits between your question and your final
            decision.
          </p>
          <p className="mt-4 text-base leading-relaxed text-gray-600">
            Instead of confusing ads or random advice, InfoBoxx gives you structured, helpful
            information — and then connects you to verified service providers when you&apos;re ready.
          </p>

          <ul className="mt-7 space-y-3">
            {BULLETS.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFD221]/25">
                  <CheckCircle2 className="text-[#c9a000]" size={14} strokeWidth={2.5} aria-hidden />
                </span>
                <span className="text-sm font-bold leading-snug text-gray-800 sm:text-[0.9375rem]">
                  {item}
                </span>
              </li>
            ))}
          </ul>

          <p className="mt-8 border-l-[3px] border-[#FFD221] pl-4 text-sm font-semibold leading-relaxed text-gray-700">
            InfoBoxx captures intent early and converts it into real action through structured content
            journeys.
          </p>

          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#FFD221] px-7 py-3 text-sm font-black text-black shadow-sm transition hover:bg-[#ffdf44]"
          >
            Read more about InfoBoxx
            <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WhatIsInfoBoxx;
